import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import jwtDecode from 'jwt-decode';

import { environment } from '@environments/environment';
import { AuthOtpContactData, AuthOtpSession } from '@core/models/auth.model';
import { HttpRequestService } from './api/http-request.service';
import { RequestResponse } from './api/interfaces/request.interface';
import { transformJsonToFormData } from '@utilities/transform-json-to-form-data';

const TOKEN_KEY = 'token-avalservi';
const EXTERNAL_ACCESS_KEY = 'avalservi-external-access';
const OTP_SESSION_KEY = 'otp-session-avalservi';

interface JwtPayload {
  user_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService extends HttpRequestService {
  private readonly url = 'auth';
  private permissions: string[] | null = null;
  userData: { initial: string; name: string } = { initial: '', name: '' };

  constructor() {
    super();
    this.getToken();
  }

  public postTokenClient(body: { username: string; password: string }): Observable<RequestResponse> {
    const clientBody = {
      ...body,
      api_username: environment.api_username,
      api_password: environment.api_password
    };

    return this.postUser<RequestResponse>(`${this.url}/client/login`, transformJsonToFormData(clientBody));
  }

  public loginWithOtp(body: { username: string; password: string }): Observable<RequestResponse> {
    const clientBody = {
      ...body,
      api_username: environment.api_username,
      api_password: environment.api_password
    };

    return this.postUser<RequestResponse>(`${this.url}/login/client/otp`, transformJsonToFormData(clientBody));
  }

  public sendOtp(body: { id: number; type_otp: number }): Observable<RequestResponse> {
    return this.postUser<RequestResponse>(`${this.url}/login/otp/send`, transformJsonToFormData(body));
  }

  public validateOtp(body: { user_id: number; code: string }, temporaryToken: string): Observable<RequestResponse> {
    return this.postUser<RequestResponse>(
      `${this.url}/login/otp/validate`,
      transformJsonToFormData(body),
      {
        headers: new HttpHeaders({
          Authorization: `Bearer ${temporaryToken}`
        })
      }
    );
  }

  public saveToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  public isExternalAccess(): boolean {
    return localStorage.getItem(EXTERNAL_ACCESS_KEY) === '1';
  }

  public clearExternalAccess(): void {
    localStorage.removeItem(EXTERNAL_ACCESS_KEY);
  }

  public getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  public saveRefreshToken(token: string): void {
    localStorage.setItem('refresh-token-avalservi', token);
  }

  public refreshToken(): Observable<RequestResponse> {
    const body = {
      user: localStorage.getItem('name'),
      token: this.getToken()
    };

    return this.postUser<RequestResponse>(`${this.url}/client/refresh`, body);
  }

  public getUserPermissions(): Observable<RequestResponse> {
    return this.get<RequestResponse>('users/permissions').pipe(
      tap((res) => {
        const data = res?.data as { permissions_list?: string[] } | undefined;
        this.permissions = data?.permissions_list ?? [];
      })
    );
  }

  public loadPermissions(): Observable<string[]> {
    if (this.permissions) {
      return of(this.permissions);
    }

    return new Observable<string[]>((observer) => {
      this.getUserPermissions().subscribe({
        next: (res) => {
          const data = res?.data as { permissions_list?: string[] } | undefined;
          observer.next(data?.permissions_list ?? []);
          observer.complete();
        },
        error: (err) => observer.error(err)
      });
    });
  }

  public hasPermission(permission: string): boolean {
    return this.permissions?.includes(permission) ?? false;
  }

  public cleanUpToken(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.permissions = null;
    this.clearOtpSession();
  }

  public override logout(): void {
    localStorage.clear();
    this.router.navigate(['/auth/iniciar-sesion']);
  }

  public set setUserData(data: { initial: string; name: string }) {
    localStorage.setItem('initial', data.initial);
    localStorage.setItem('name', data.name);
  }

  public get getUserData(): { initial: string; name: string } {
    this.userData = {
      initial: localStorage.getItem('initial') ?? '',
      name: localStorage.getItem('name') ?? ''
    };
    return this.userData;
  }

  public saveOtpSession(session: AuthOtpSession): void {
    localStorage.setItem(OTP_SESSION_KEY, JSON.stringify(session));
  }

  public getOtpSession(): AuthOtpSession | null {
    const rawSession = localStorage.getItem(OTP_SESSION_KEY);

    if (!rawSession) {
      return null;
    }

    try {
      return JSON.parse(rawSession) as AuthOtpSession;
    } catch {
      this.clearOtpSession();
      return null;
    }
  }

  public updateOtpSession(patch: Partial<AuthOtpSession>): AuthOtpSession | null {
    const currentSession = this.getOtpSession();

    if (!currentSession) {
      return null;
    }

    const nextSession = { ...currentSession, ...patch };
    this.saveOtpSession(nextSession);
    return nextSession;
  }

  public clearOtpSession(): void {
    localStorage.removeItem(OTP_SESSION_KEY);
  }

  public getOtpUserIdFromToken(token: string): number | null {
    try {
      const payload = jwtDecode<JwtPayload>(token);
      return payload.user_id ?? null;
    } catch {
      return null;
    }
  }

  public getOtpContactData(response: RequestResponse): AuthOtpContactData | null {
    const payload = response?.data as { data?: AuthOtpContactData } | undefined;
    return payload?.data ?? null;
  }
}
